import { track } from "@builder.io/sdk-vue";

const BUILDER_API_KEY = "5c32841cdfc646708f6dc9b494dadddc";
const STARTED_CUSTOMIZER_EVENT = "Started Customizer";
const STARTED_CHECKOUT_EVENT = "Started Checkout";
const STARTED_CUSTOMIZER_SESSION_PREFIX = "builderTracking:startedCustomizer";
const CHECKOUT_TRACKING_TIMEOUT_MS = 700;

function getSessionStorageItem(key) {
    try {
        return window.sessionStorage?.getItem(key) || "";
    } catch (_) {
        // sentry-ignore: tracking must never block the customizer
        return "";
    }
}

function setSessionStorageItem(key, value) {
    try {
        window.sessionStorage?.setItem(key, value);
    } catch (_) {
        // sentry-ignore: tracking must never block the customizer
    }
}

function getContentId(content) {
    return content?.id || "";
}

function getVariationId(content) {
    const contentId = getContentId(content);
    const variationId = content?.testVariationId || content?.variationId || "";

    return variationId && variationId !== contentId ? variationId : "";
}

function getRouteMetadata(vm) {
    const route = vm?.$route;
    const bookId = route?.params?.bookId || "unknown_book";

    return {
        bookId,
        routeName: route?.name || "unknown_step",
        routePath: route?.path || "",
        pagePath: route?.path ? `/${bookId}${route.path}` : "",
    };
}

function getBaseMetadata(vm, content, metadata = {}) {
    const pageSettings = vm?.pageSettings || {};

    return {
        source: "customizer-vue",
        component: vm?.$options?.name || "unknown_component",
        host: window.location.host,
        path: window.location.pathname,
        hash: window.location.hash,
        language: document.documentElement.lang || "",
        product: pageSettings.product || "",
        variant: pageSettings.variant || "",
        contentId: getContentId(content),
        variationId: getVariationId(content),
        contentName: content?.name || content?.data?.title || "",
        ...getRouteMetadata(vm),
        ...metadata,
    };
}

function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function trackBuilderEvent(eventName, { vm, content, metadata = {} } = {}) {
    const contentId = getContentId(content);
    const variationId = getVariationId(content);
    const eventData = {
        type: eventName,
        apiKey: BUILDER_API_KEY,
        metadata: getBaseMetadata(vm, content, metadata),
    };

    if (contentId) {
        eventData.contentId = contentId;
    }

    if (variationId) {
        eventData.variationId = variationId;
    }

    // sentry-ignore: tracking must never block the customizer
    return track(eventData).catch(() => {});
}

export function trackStartedCustomizer({ vm, content, stepName } = {}) {
    const contentId = getContentId(content) || "unknown_content";
    const variationId = getVariationId(content) || "default";
    const dedupeKey = [
        STARTED_CUSTOMIZER_SESSION_PREFIX,
        window.location.host,
        contentId,
        variationId,
    ].join(":");

    if (getSessionStorageItem(dedupeKey) === "1") {
        return Promise.resolve();
    }

    setSessionStorageItem(dedupeKey, "1");

    return trackBuilderEvent(STARTED_CUSTOMIZER_EVENT, {
        vm,
        content,
        metadata: {
            stepName,
        },
    });
}

export function trackStartedCheckout({ vm, content, items, checkoutFlow } = {}) {
    const trackingPromise = trackBuilderEvent(STARTED_CHECKOUT_EVENT, {
        vm,
        content,
        metadata: {
            checkoutFlow,
            itemCount: items?.length || 0,
            itemVariantIds: (items || [])
                .filter((item) => item?.id)
                .map((item) => String(item.id)),
        },
    });

    return Promise.race([
        trackingPromise,
        wait(CHECKOUT_TRACKING_TIMEOUT_MS),
    ]);
}
